import jsPDF from "jspdf";
import "jspdf-autotable";
import Logo from "../../assets/nhclogo.png";

const PDF_STYLES = {
    colors: {
        primary: '#1e85e5',
        textDark: '#1a1a1a',
        textMuted: '#666666',
        textLight: '#888888',
        borderLight: '#e0e0e0',
        backgroundAlt: '#f8f9fa'
    },
    fontSize: {
        title: 16,
        subtitle: 13,
        sectionHeader: 12,
        body: 10,
        tableCell: 8,
        footer: 8
    }
};

export const formatDate = (date) =>
    date.toISOString().split("T")[0];

export const formatData = (report) => {
    const tableData = [];
    const processEntry = (key, value) => {
        if (["spendingAccounts", "enrollmentFee", "teladoc", "corehealth"].includes(key)) {
            tableData.push(...value);
        } else if (
            ["teladocTotal", "corehealthTotal", "spendingAccountsTotal", "enrollmentFeeTotal"].includes(key)
        ) {
            if (parseFloat(value) > 0) tableData.push({ [key]: value });
        } else {
            tableData.push({ [key]: value });
        }
    };

    if (Array.isArray(report)) {
        report.forEach((item) => Object.entries(item).forEach(([key, value]) => processEntry(key, value)));
    } else {
        Object.entries(report).forEach(([key, value]) => processEntry(key, value));
    }

    return tableData;
};

const addSummaryTable = (doc, summaryRows, startY) => {
    doc.autoTable({
        head: [["Type", "Amount"]],
        body: summaryRows,
        startY,
        columnStyles: { 1: { halign: "right" } },
        didParseCell: (data) => {
            if (data.section === "head" && data.column.index === 1) {
                data.cell.styles.halign = "right";
            }
            if (data.row.index === summaryRows.length - 1) {
                data.cell.styles.fontStyle = "bold";
            }
        },
    });
};

const addDetailTable = (doc, title, items, startY) => {
    if (!items?.length) return startY;
    const rows = items.map((x) => [x.description, `$${x.amount}`, x.date]);

    doc.setFontSize(PDF_STYLES.fontSize.sectionHeader);
    doc.setTextColor(PDF_STYLES.colors.primary);
    doc.text(title, 15, startY + 15);
    doc.autoTable({
        head: [["Description", "Amount", "Date"]],
        body: rows,
        startY: startY + 20,
        columnStyles: { 1: { halign: "right" } },
    });

    return doc.previousAutoTable.finalY;
};

export const generatePDF = (report, summaryInfo, fromDate, toDate) => {
    const doc = new jsPDF();
    const docTitle = "Commission Report";

    jsPDF.autoTableSetDefaults({
        theme: "striped",
        tableWidth: "auto",
        columnWidth: "auto",
        showHead: "everyPage",
        rowPageBreak: "avoid",
        pageBreak: "auto",
        margin: { top: 40, bottom: 25 },
        headStyles: {
            fillColor: PDF_STYLES.colors.primary,
            textColor: '#ffffff',
            fontSize: PDF_STYLES.fontSize.tableCell,
            fontStyle: 'bold'
        },
        alternateRowStyles: {
            fillColor: PDF_STYLES.colors.backgroundAlt
        },
        styles: {
            overflow: "linebreak",
            cellPadding: 4,
            fontSize: PDF_STYLES.fontSize.tableCell,
            lineColor: PDF_STYLES.colors.borderLight,
            lineWidth: 0.25,
            textColor: PDF_STYLES.colors.textDark
        },
        didDrawPage: function (data) {
            const pageWidth = doc.internal.pageSize.getWidth();
            const pageHeight = doc.internal.pageSize.getHeight();

            // header strip
            doc.setFillColor(PDF_STYLES.colors.primary);
            doc.rect(0, 0, pageWidth, 6, 'F');

            // logo
            doc.addImage(Logo, "PNG", 160, 12, 0, 12);

            // footer
            doc.setFontSize(PDF_STYLES.fontSize.footer);
            doc.setTextColor(PDF_STYLES.colors.textLight);
            doc.text(docTitle, 15, pageHeight - 10);
            doc.text(
                `Page ${doc.internal.getCurrentPageInfo().pageNumber} of ${doc.internal.getNumberOfPages()}`,
                pageWidth - 15,
                pageHeight - 10,
                { align: "right" }
            );
        },
    });

    const width = doc.internal.pageSize.getWidth();

    // title section
    doc.setFontSize(PDF_STYLES.fontSize.title);
    doc.setTextColor(PDF_STYLES.colors.textDark);
    doc.text(report[0].distroAccountName, width / 2, 30, { align: "center" });

    doc.setFontSize(PDF_STYLES.fontSize.subtitle);
    doc.setTextColor(PDF_STYLES.colors.textMuted);
    doc.text(`Distribution Report ${fromDate} - ${toDate}`, width / 2, 38, { align: "center" });

    // Summary info section
    if (summaryInfo?.length) {
        const summaryRows = summaryInfo.map((x) => [x.type, `$${x.amount.toFixed(2)}`]);
        doc.setFontSize(PDF_STYLES.fontSize.sectionHeader);
        doc.setTextColor(PDF_STYLES.colors.primary);
        doc.text("Summary Report", 15, 52);
        addSummaryTable(doc, summaryRows, 57);
    }

    const addReport = (data, startY = 70) => {
        const summaryRows = [
            ...(parseFloat(data.spendingAccountsTotal) > 0
                ? [["Spending Accounts Total", `$${data.spendingAccountsTotal}`]]
                : []),
            ...(parseFloat(data.enrollmentFeeTotal) > 0
                ? [["Enrollment Fee Total", `$${data.enrollmentFeeTotal}`]]
                : []),
            ...(parseFloat(data.teladocTotal) > 0 ? [["Teladoc Total", `$${data.teladocTotal}`]] : []),
            ...(parseFloat(data.corehealthTotal) > 0
                ? [["Corehealth Total", `$${data.corehealthTotal}`]]
                : []),
            ["Total", `$${data.total}`],
        ];

        doc.setFontSize(PDF_STYLES.fontSize.sectionHeader);
        doc.setTextColor(PDF_STYLES.colors.primary);
        doc.text("Summary Report", 15, startY);
        addSummaryTable(doc, summaryRows, startY + 5);

        let finalY = doc.previousAutoTable.finalY;
        finalY = addDetailTable(doc, "Spending Accounts Report", data.spendingAccounts, finalY);
        finalY = addDetailTable(doc, "Enrollment Fee Report", data.enrollmentFee, finalY);
        finalY = addDetailTable(doc, "Teladoc Report", data.teladoc, finalY);
        finalY = addDetailTable(doc, "Corehealth Report", data.corehealth, finalY);
    };

    if (Array.isArray(report)) {
        report.forEach((item, idx) => {
            let finalY = doc.previousAutoTable?.finalY || 80;
            const pageHeight = doc.internal.pageSize.getHeight();
            if (idx !== 0 && pageHeight - finalY < 100) {
                doc.addPage();
                finalY = 20;
            }
            const name = item.company ? "Company" : "Agent";
            doc.setFontSize(PDF_STYLES.fontSize.title);
            doc.setTextColor(PDF_STYLES.colors.textDark);
            doc.text(`${name}: ${item.company || item.agentReportingCode}`, 15, finalY + 20);
            addReport(item, finalY + 25);
        });
    } else {
        addReport(report);
    }

    const dateStr = new Date().toDateString().replace(/\s+/g, "");
    doc.save(`Commission Report ${report[0].distroAccountName} ${dateStr}.pdf`);
};
